import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { projectService } from '../services/api';
import './Projects.css';

const Projects = ({ user }) => {
  const navigate = useNavigate();
  const canManageProjects = user?.role === 'admin';
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    min_people_required: 2,
    accommodation_price: 0, // Starý formát pre kompatibilitu
    accommodation_price_monthly: 0, // Nová mesačná cena
    people_with_accommodation: 0,
    weekdays_count: 5, // Nové pole - počet pracovných dní v týždni
    weekend_days_count: 2, // Nové pole - počet víkendových dní v týždni
    weekday_hours: 9, // Nové pole - hodiny cez týždeň
    weekend_hours: 12, // Nové pole - hodiny cez víkend
    working_days_per_month: 22, // Zachované pre kompatibilitu
    working_hours_per_day: 8, // Zachované pre kompatibilitu
    rotation_weeks_work: 4,
    rotation_weeks_home: 2
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      min_people_required: 2,
      accommodation_price: 0,
      accommodation_price_monthly: 0,
      people_with_accommodation: 0,
      weekdays_count: 5,
      weekend_days_count: 2,
      weekday_hours: 9,
      weekend_hours: 12,
      working_days_per_month: 22,
      working_hours_per_day: 8,
      rotation_weeks_work: 4,
      rotation_weeks_home: 2
    });
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const { items, total: t } = await projectService.getAllPaged({ page, page_size: pageSize, q: search });
      setProjects(items);
      setTotal(t);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Nepodarilo sa načítať projekty. Skúste to znova.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadProjects();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, pageSize, search]);

  if (!user) {
    return (
      <div className="projects-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Načítavam používateľské údaje...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingProject) {
        await projectService.update(editingProject.id, formData);
      } else {
        await projectService.create(formData);
      }

      await loadProjects();
      setShowAddForm(false);
      setEditingProject(null);
      resetForm();
      toast.success(editingProject ? 'Projekt bol úspešne upravený.' : 'Projekt bol úspešne pridaný.');
    } catch (error) {
      console.error('Error saving project:', error);
      const details = error.response?.data?.details;
      const msg = Array.isArray(details) && details.length ? details.map(d => d.msg || d.param).join(', ') : (error.response?.data?.error || 'Nastala chyba pri ukladaní projektu');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (projectToEdit) => {
    setEditingProject(projectToEdit);
    setFormData({
      name: projectToEdit.name,
      description: projectToEdit.description,
      min_people_required: projectToEdit.min_people_required || 2,
      accommodation_price: projectToEdit.accommodation_price || 0,
      accommodation_price_monthly: projectToEdit.accommodation_price_monthly || 0,
      people_with_accommodation: projectToEdit.people_with_accommodation || 0,
      weekdays_count: projectToEdit.weekdays_count || 5,
      weekend_days_count: projectToEdit.weekend_days_count || 2,
      weekday_hours: projectToEdit.weekday_hours || 9,
      weekend_hours: projectToEdit.weekend_hours || 12,
      working_days_per_month: projectToEdit.working_days_per_month || 22,
      working_hours_per_day: projectToEdit.working_hours_per_day || 8,
      rotation_weeks_work: projectToEdit.rotation_weeks_work || 4,
      rotation_weeks_home: projectToEdit.rotation_weeks_home || 2
    });
    setShowAddForm(true);
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openProjectDetail = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleDeleteConfirmed = async () => {
    if (!projectToDelete) return;

    try {
      await projectService.delete(projectToDelete.id);
      toast.success('Projekt bol úspešne vymazaný.');
      setProjectToDelete(null);
      await loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Nastala chyba pri mazaní projektu.');
    }
  };

  if (loading) {
    return (
      <div className="projects-container">
        <div className="projects-header">
          <h1 className="projects-title">📋 Správa projektov</h1>
        </div>
        <div className="projects-skeleton-grid" aria-label="Načítavam projekty">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div key={item} className="project-card project-card-skeleton">
              <div className="skeleton skeleton-title" />
              <div className="skeleton skeleton-line" />
              <div className="skeleton skeleton-line short" />
              <div className="skeleton skeleton-button-row" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="projects-container">
      <div className="projects-header">
        <h1 className="projects-title">📋 Správa projektov</h1>
      </div>

      <div className="projects-toolbar">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="🔎 Hľadať projekty..."
          className="projects-search-input"
        />
        <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value) || 12); setPage(1); }} className="projects-page-size">
          <option value={6}>6 / strana</option>
          <option value={12}>12 / strana</option>
          <option value={24}>24 / strana</option>
        </select>
        {canManageProjects && (
          <button className="add-project-btn" onClick={() => { resetForm(); setEditingProject(null); setShowAddForm(true); }}>➕ Pridať projekt</button>
        )}
      </div>

      {projects.length === 0 ? (
        <div className="projects-empty-state">
          <div className="projects-empty-icon">🗂️</div>
          <h2>{search ? 'Nenašli sa žiadne projekty' : 'Zatiaľ tu nie sú projekty'}</h2>
          <p>{search ? 'Skúste upraviť vyhľadávanie alebo vymazať filter.' : 'Po vytvorení projektu sa tu zobrazí jeho karta, rotácia a detail.'}</p>
          {search && <button className="detail-btn" onClick={() => setSearch('')}>Vymazať vyhľadávanie</button>}
          {!search && canManageProjects && <button className="add-project-btn" onClick={() => setShowAddForm(true)}>➕ Pridať prvý projekt</button>}
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map(project => (
            <div key={project.id} className="project-card">
              <div className="project-info">
                <div className="project-header">
                  <div className="project-icon">📋</div>
                  <h3 className="project-name">{project.name}</h3>
                </div>
                <p className="project-description">{project.description || 'Bez popisu'}</p>
                <div className="project-stats">
                  <span className="stat">📅 Vytvorený: {project.created_at ? new Date(project.created_at).toLocaleDateString('sk-SK') : 'Neznámy dátum'}</span>
                  {project.min_people_required && <span className="stat">👥 Min. ľudí v práci: {project.min_people_required}</span>}
                  {project.accommodation_price_monthly !== undefined && (
                    <span className="stat">🏠 Ubytovanie: {
                      project.accommodation_price_monthly > 0
                        ? `${project.accommodation_price_monthly}€/mes (${project.accommodation_hourly_rate ? project.accommodation_hourly_rate.toFixed(2) : '0'}€/h)`
                        : (project.accommodation_price ? `${project.accommodation_price}€/deň` : 'Nezadané')
                    }</span>
                  )}
                  {project.rotation_weeks_work && <span className="stat">🔄 Rotácia: {project.rotation_weeks_work}t práca / {project.rotation_weeks_home || 2}t doma</span>}
                </div>
              </div>
              <div className="project-actions">
                <button className="detail-btn" onClick={() => openProjectDetail(project.id)}>👁️ Detail</button>
                {canManageProjects && (
                  <>
                    <button className="edit-btn" onClick={() => handleEdit(project)}>✏️ Upraviť</button>
                    <button className="delete-btn" onClick={() => setProjectToDelete(project)}>🗑️ Vymazať</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}


      <div className="pagination projects-pagination">
        <div>
          Spolu: {total} projektov
        </div>
        <div className="projects-pagination-actions">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>← Predošlá</button>
          <span>Strana {page}</span>
          <button disabled={(page * pageSize) >= total} onClick={() => setPage((p) => p + 1)}>Ďalšia →</button>
        </div>
      </div>

      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{editingProject ? '✏️ Upraviť projekt' : '➕ Pridať nový projekt'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Názov projektu:</label>
                <input
                  type="text"
                  name="name"

                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Zadajte názov projektu"
                />
              </div>
              
              <div className="form-group">
                <label>Popis projektu:</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Zadajte popis projektu"
                />
              </div>
              
              <div className="form-group">
                <label>Minimálny počet ľudí v práci:</label>
                <input
                  type="number"
                  name="min_people_required"
                  value={formData.min_people_required}
                  onChange={handleInputChange}
                  min="2"
                  max="50"
                  required
                  placeholder="Zadajte minimálny počet ľudí (min. 2)"
                />
                <small className="form-help">
                  Koľko ľudí musí byť minimálne v práci súčasne (min. 2 pre rotáciu)
                </small>
              </div>
              
              {/* Nová sekcia pre ubytovanie s mesačnou cenou */}
              <div className="accommodation-section">
                <h4 style={{color: '#2196F3', marginBottom: '15px'}}>🏠 Nastavenia ubytovania</h4>
                
                <div className="form-group">
                  <label>Mesačná cena ubytovania (€):</label>
                  <input
                    type="number"
                    name="accommodation_price_monthly"
                    value={formData.accommodation_price_monthly}
                    onChange={handleInputChange}
                    min="0"
                    step="1"
                    placeholder="Zadajte mesačnú cenu ubytovania"
                  />
                  <small className="form-help">
                    Celková mesačná cena za ubytovanie v eurách
                  </small>
                </div>
                
                <div className="form-group">
                  <label>Počet ľudí s ubytovaním:</label>
                  <input
                    type="number"
                    name="people_with_accommodation"
                    value={formData.people_with_accommodation}
                    onChange={handleInputChange}
                    min="0"
                    max="20"
                    placeholder="Koľko ľudí potrebuje ubytovanie"
                  />
                  <small className="form-help">
                    Počet zamestnancov, ktorí potrebujú ubytovanie
                  </small>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Dni cez týždeň:</label>
                    <input
                      type="number"
                      name="weekdays_count"
                      value={formData.weekdays_count}
                      onChange={handleInputChange}
                      min="1"
                      max="7"
                      placeholder="5"
                    />
                    <small className="form-help">
                      Počet pracovných dní v týždni (1-7)
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Dni cez víkend:</label>
                    <input
                      type="number"
                      name="weekend_days_count"
                      value={formData.weekend_days_count}
                      onChange={handleInputChange}
                      min="0"
                      max="7"
                      placeholder="2"
                    />
                    <small className="form-help">
                      Počet víkendových dní v týždni (0-7)
                    </small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Hodiny cez týždeň:</label>
                    <input
                      type="number"
                      name="weekday_hours"
                      value={formData.weekday_hours}
                      onChange={handleInputChange}
                      min="1"
                      max="16"
                      placeholder="9"
                    />
                    <small className="form-help">
                      Počet hodín práce v pracovné dni
                    </small>
                  </div>
                  
                  <div className="form-group">
                    <label>Hodiny cez víkend:</label>
                    <input
                      type="number"
                      name="weekend_hours"
                      value={formData.weekend_hours}
                      onChange={handleInputChange}
                      min="1"
                      max="16"
                      placeholder="12"
                    />
                    <small className="form-help">
                      Počet hodín práce cez víkend
                    </small>
                  </div>
                </div>
                
                {formData.accommodation_price_monthly > 0 && formData.people_with_accommodation > 0 && (
                  <div className="calculation-preview" style={{
                    background: '#e3f2fd', 
                    padding: '10px', 
                    borderRadius: '5px', 
                    marginTop: '10px',
                    border: '1px solid #2196F3'
                  }}>
                    <strong>Prepočet nákladov na ubytovanie:</strong><br/>
                    <div style={{fontSize: '0.9em', marginTop: '5px'}}>
                      • Týždeň: {formData.weekdays_count} dní × {formData.weekday_hours}h = {formData.weekdays_count * formData.weekday_hours}h<br/>
                      • Víkend: {formData.weekend_days_count} dní × {formData.weekend_hours}h = {formData.weekend_days_count * formData.weekend_hours}h<br/>
                      • <strong>Spolu za týždeň: {formData.weekdays_count * formData.weekday_hours + formData.weekend_days_count * formData.weekend_hours}h</strong>
                    </div>
                    <div style={{marginTop: '8px', padding: '5px', background: '#fff', borderRadius: '3px'}}>
                      {formData.accommodation_price_monthly}€ ÷ {formData.people_with_accommodation} min.ľudí ÷ 30 dní × 7 ÷ {formData.weekdays_count * formData.weekday_hours + formData.weekend_days_count * formData.weekend_hours}h/týždeň = 
                      <span style={{color: '#2196F3', fontWeight: 'bold'}}>
                        {(formData.accommodation_price_monthly / formData.people_with_accommodation / 30 * 7 / (formData.weekdays_count * formData.weekday_hours + formData.weekend_days_count * formData.weekend_hours)).toFixed(4)}€/h
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Týždne v práci:</label>
                <input
                  type="number"
                  name="rotation_weeks_work"
                  value={formData.rotation_weeks_work}
                  onChange={handleInputChange}
                  min="1"
                  max="12"
                  required
                  placeholder="Zadajte počet týždňov v práci"
                />
                <small className="form-help">
                  Koľko týždňov pracuje zamestnanec pred odchodom domov
                </small>
              </div>
              
              <div className="form-group">
                <label>Týždne doma:</label>
                <input
                  type="number"
                  name="rotation_weeks_home"
                  value={formData.rotation_weeks_home}
                  onChange={handleInputChange}
                  min="1"
                  max="8"
                  required
                  placeholder="Zadajte počet týždňov doma"
                />
                <small className="form-help">
                  Koľko týždňov je zamestnanec doma medzi rotáciami
                </small>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="save-btn" disabled={saving}>
                  {saving ? 'Ukladám...' : (editingProject ? '💾 Uložiť zmeny' : '➕ Pridať projekt')}
                </button>
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProject(null);
                    resetForm();
                  }}
                >
                  ❌ Zrušiť
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {projectToDelete && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-project-title">
          <div className="modal confirm-modal">
            <div className="confirm-icon">⚠️</div>
            <h2 id="delete-project-title">Vymazať projekt?</h2>
            <p>
              Projekt <strong>{projectToDelete.name}</strong> bude odstránený. Táto akcia sa nedá vrátiť späť.
            </p>
            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={() => setProjectToDelete(null)}>Zrušiť</button>
              <button type="button" className="delete-btn" onClick={handleDeleteConfirmed}>Áno, vymazať</button>
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

export default Projects;